import { z } from 'zod';
import { parseMoney } from './money';

/**
 * Validators for decimal values that must stay strings.
 *
 * A monetary or percentage input is a text field, never `<input type=number>`:
 * a number input hands back a JS float, and the whole app is built on the rule
 * that money never becomes one. The value is validated as a decimal string
 * with decimal.js and submitted as a string.
 */

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

export function isDecimalString(value: string): boolean {
  if (!DECIMAL_RE.test(value.trim())) return false;
  return parseMoney(value.trim()) !== null;
}

/** A decimal string, optionally constrained to a range (bounds as strings). */
export function decimalString(options: {
  message?: string;
  min?: string;
  max?: string;
  positive?: boolean;
}): z.ZodEffects<z.ZodString, string, string> {
  const message = options.message ?? 'Введите число, например 12.5';
  return z.string().superRefine((raw, ctx) => {
    const value = raw.trim();
    if (!isDecimalString(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
      return;
    }
    const decimal = parseMoney(value);
    if (decimal === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
      return;
    }
    if (options.positive === true && decimal.lessThanOrEqualTo(0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Значение должно быть больше нуля.' });
    }
    if (options.min !== undefined && decimal.lessThan(options.min)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Значение не может быть меньше ${options.min}.`,
      });
    }
    if (options.max !== undefined && decimal.greaterThan(options.max)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Значение не может быть больше ${options.max}.`,
      });
    }
  });
}

/** An integer typed into a `type=number` input (never used for money). */
export function integerInRange(min: number, max: number, unitRu: string): z.ZodType<number> {
  return z
    .number({ invalid_type_error: `Введите целое число (${unitRu}).` })
    .int('Значение должно быть целым числом.')
    .min(min, `Не меньше ${String(min)}.`)
    .max(max, `Не больше ${String(max)}.`);
}

/** A non-integer numeric threshold (RSI levels), also never money. */
export function numberInRange(min: number, max: number): z.ZodType<number> {
  return z
    .number({ invalid_type_error: 'Введите число.' })
    .min(min, `Не меньше ${String(min)}.`)
    .max(max, `Не больше ${String(max)}.`);
}
